import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Globe, User, GitBranch, Sparkles } from 'lucide-react';
import { Badge } from '../ui/Badge';
interface GlobalCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}
export function GlobalCommandPalette({
  isOpen,
  onClose
}: GlobalCommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'my-streams' | 'org' | 'people'>('all');
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, 5));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);
  // Mock results based on query
  const hasQuery = query.length > 0;
  const isQuestion = query.includes('?') || query.toLowerCase().startsWith('how') || query.toLowerCase().startsWith('what');
  return <AnimatePresence>
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-2xl bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[70vh]">
            {/* Search Input */}
            <div className="p-4 border-b border-surface-600 flex items-center gap-3">
              <Search className="w-5 h-5 text-teal-400" />
              <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search streams, ask anything, find experts..." className="flex-1 bg-transparent border-none text-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-0" autoFocus />
              <div className="flex items-center gap-2">
                <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded bg-surface-800 border border-surface-500 text-xs text-text-secondary font-sans">
                  <span className="text-[10px]">ESC</span>
                </kbd>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center px-4 pt-3 pb-0 border-b border-surface-600 gap-6 overflow-x-auto">
              {[{
            id: 'all',
            label: 'All'
          }, {
            id: 'my-streams',
            label: 'My Streams'
          }, {
            id: 'org',
            label: 'Org Search'
          }, {
            id: 'people',
            label: 'Ask Anyone'
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as 'all' | 'my-streams' | 'org' | 'people')} className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-teal-500 text-teal-400' : 'border-transparent text-text-muted hover:text-text-primary'}`}>
                  {tab.label}
                </button>)}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-2">
              {!hasQuery ? <div className="p-4 space-y-6">
                  {/* Recent Searches */}
                  <div>
                    <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-3 px-2">
                      Recent
                    </h3>
                    <div className="space-y-1">
                      {[{
                  icon: Search,
                  text: 'API performance metrics'
                }, {
                  icon: User,
                  text: 'Sarah Chen'
                }, {
                  icon: GitBranch,
                  text: 'DNS Optimization'
                }].map((item, i) => <button key={i} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-800 text-left group transition-colors">
                          <item.icon className="w-4 h-4 text-text-muted group-hover:text-text-secondary" />
                          <span className="text-text-primary group-hover:text-text-primary text-sm">
                            {item.text}
                          </span>
                        </button>)}
                    </div>
                  </div>

                  {/* Suggested Queries */}
                  <div>
                    <h3 className="text-xs font-medium text-text-muted uppercase tracking-wider mb-3 px-2">
                      Suggested
                    </h3>
                    <div className="space-y-1">
                      {["Who's working on performance optimization?", 'Find streams related to caching', 'What did we learn about Redis last quarter?'].map((text, i) => <button key={i} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-800 text-left group transition-colors">
                          <Sparkles className="w-4 h-4 text-teal-500/70 group-hover:text-teal-400" />
                          <span className="text-text-primary group-hover:text-text-primary text-sm">
                            {text}
                          </span>
                        </button>)}
                    </div>
                  </div>
                </div> : <div className="space-y-4 p-2">
                  {/* AI Answer Section (if question) */}
                  {isQuestion && <div className="mx-2 mb-4 p-4 rounded-xl bg-teal-500/10 border border-teal-500/20">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-teal-400" />
                        <span className="text-xs font-bold text-teal-400 uppercase tracking-wider">
                          AI Answer
                        </span>
                      </div>
                      <p className="text-sm text-text-primary leading-relaxed mb-3">
                        Based on recent streams, the team is using a{' '}
                        <strong>write-through caching strategy</strong> with
                        Redis. This was decided in the "DNS Optimization" stream
                        to reduce database load by 40%.
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-text-muted">Sources:</span>
                        <Badge variant="outline" className="text-[10px] bg-surface-900/50 border-surface-500 text-text-secondary">
                          DNS Optimization
                        </Badge>
                        <Badge variant="outline" className="text-[10px] bg-surface-900/50 border-surface-500 text-text-secondary">
                          Architecture Review
                        </Badge>
                      </div>
                    </div>}

                  {/* Search Results */}
                  <div className="space-y-1">
                    {/* Streams */}
                    <div className="px-3 py-1.5 text-xs font-medium text-text-muted uppercase tracking-wider">
                      Streams
                    </div>
                    {[{
                name: 'DNS Optimization Project',
                status: 'Active',
                owner: 'Alex M.'
              }, {
                name: 'Redis Migration',
                status: 'Completed',
                owner: 'Sarah C.'
              }].map((item, i) => <button key={i} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left group transition-colors ${selectedIndex === i ? 'bg-surface-800' : 'hover:bg-surface-800'}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center text-purple-400">
                            <GitBranch className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-text-primary">
                              {item.name}
                            </div>
                            <div className="text-xs text-text-muted">
                              Owned by {item.owner}
                            </div>
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-[10px]">
                          {item.status}
                        </Badge>
                      </button>)}

                    {/* People */}
                    <div className="px-3 py-1.5 mt-2 text-xs font-medium text-text-muted uppercase tracking-wider">
                      People
                    </div>
                    {[{
                name: 'Sarah Chen',
                role: 'Staff Engineer',
                relevance: 'High match'
              }, {
                name: 'Mike Ross',
                role: 'Product Lead',
                relevance: 'Medium match'
              }].map((item, i) => <button key={i + 2} className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left group transition-colors ${selectedIndex === i + 2 ? 'bg-surface-800' : 'hover:bg-surface-800'}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium text-xs">
                            {item.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-text-primary">
                              {item.name}
                            </div>
                            <div className="text-xs text-text-muted">
                              {item.role}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-teal-400">
                          {item.relevance}
                        </span>
                      </button>)}
                  </div>
                </div>}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-surface-600 bg-surface-900/50 flex items-center justify-between text-xs text-text-muted">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-surface-800 border border-surface-500 font-sans">
                    ↵
                  </kbd>
                  to select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 rounded bg-surface-800 border border-surface-500 font-sans">
                    ↑↓
                  </kbd>
                  to navigate
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-3 h-3 text-teal-500" />
                <span>Global Search</span>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}